import { IconLogout, IconUserFilled } from "@tabler/icons-react";
import { Form, Input, Modal, Popover, Select, message } from "antd";
import { useEffect, useState } from "react";

// import Feature from "@/components/Feature";
import NotificationCenter from "@/components/NotificationCenter";
import { apiUserGetMe } from "@/api";
import { authClient } from "@/lib/auth-clients";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface UserData {
  id: string;
  username: string;
  email: string;
  provider: string;
  confirmed: string;
  documentId: string;
  blocked: string;
  countryAndRegion: string;
  phoneNumber: string;
}

const Header = () => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();
  const {
    id = "",
    username = "",
    email = "",
    provider = "",
    confirmed = "",
    documentId = "",
    blocked = "",
    countryAndRegion = "",
    phoneNumber = "",
  } = userData || {};

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiUserGetMe();
        setUserData(response);
      } catch (error) {
        console.error("Error_apiUserGetMe:", error);
      }
    };

    fetchData();
  }, []);

  const handleLanguageChange = (value: string) => {
    i18n.changeLanguage(value);
  };

  const handleLogout = async () => {
    await authClient.signOut();
    window.localStorage.clear();
    navigate("/login");
  };

  const handleUpdatePassword = async (values: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error(t("header.passwordMismatch"));
      return;
    }
    setPasswordLoading(true);
    const { error } = await authClient.changePassword({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
      revokeOtherSessions: false,
    });
    setPasswordLoading(false);
    if (error) {
      message.error(t("header.passwordUpdateFailed"));
    } else {
      message.success(t("header.passwordUpdateSuccess"));
      setPasswordModalOpen(false);
      form.resetFields();
    }
  };

  return (
    <header className="flex h-20 items-center justify-between bg-white px-8 sticky top-0 z-50">
      <div className="flex w-full flex-row items-center justify-end">
        <NotificationCenter />
        <div className="ml-4 flex flex-row items-center cursor-pointer">
          <div
            onClick={() => {
              navigate(`/companyListPage`);
            }}
            className="mr-1 text-lg text-gray-800"
          >
            {t("header.backToCompanyList")}
          </div>
          <IconLogout />
        </div>
        <div className="ml-4 flex flex-row items-center cursor-pointer">
          <div onClick={handleLogout} className="mr-1 text-lg text-gray-800">
            {t("header.logout")}
          </div>
          <IconLogout />
        </div>
        <Popover
          className="cursor-pointer"
          placement="bottomRight"
          title={
            <>
              <div>ID: {documentId}</div>
              <div>Email: {email ? email : t("header.notSet")}</div>
              <div
                className="cursor-pointer text-blue-500 hover:text-blue-700 mt-1"
                onClick={() => setPasswordModalOpen(true)}
              >
                {t("header.updatePassword")}
              </div>
            </>
          }
        >
          <div className="ml-4 flex flex-row items-center">
            <div className="text-lg text-gray-800">{username}</div>
            <IconUserFilled />
          </div>
        </Popover>
        <Modal
          open={passwordModalOpen}
          title={t("header.updatePassword")}
          onCancel={() => {
            setPasswordModalOpen(false);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          confirmLoading={passwordLoading}
          okText={t("header.updatePassword")}
        >
          <Form form={form} onFinish={handleUpdatePassword} layout="vertical">
            <Form.Item name="currentPassword" label={t("header.currentPassword")} rules={[{ required: true }]}>
              <Input.Password />
            </Form.Item>
            <Form.Item name="newPassword" label={t("header.newPassword")} rules={[{ required: true }]}>
              <Input.Password />
            </Form.Item>
            <Form.Item name="confirmPassword" label={t("header.confirmPassword")} rules={[{ required: true }]}>
              <Input.Password />
            </Form.Item>
          </Form>
        </Modal>
        <div className="ml-4">
          <Select
            value={i18n.language}
            onChange={handleLanguageChange}
            options={[
              { value: "en", label: "English" },
              { value: "zh", label: "中文" },
            ]}
            style={{ width: 100 }}
          />
        </div>
      </div>
    </header>
  );
};

export default Header;
